import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box, Toolbar, CircularProgress } from "@mui/material";
import theme from "./theme/theme";
import Navbar from "./components/Navbar";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AuditDashboard from "./pages/AuditDashboard";
import ProgressOverview from "./pages/ProgressOverview";
import CbamDashboard from "./components/CbamDashboard";

// Auth Provider (NEW)
import { AuthProvider, useAuth } from "./auth/AuthContext";

// Context Provider
import { FormProvider } from "./context/FormContext";

import { ProductManualProvider } from "./context/ProductManualContext";

// Components
import Home from "./components/Home";
import AdminChecklist from "./pages/AdminChecklist";
import UserChecklistWrapper from "./components/UserChecklistWrapper";
import UserManagement from "./components/UserManagement";
import TenantManagement from "./components/TenantManagement";
import Footer from "./components/Footer";
import LandingPg from "./pages/LandingPg";
import TnC from "./pages/TnC";
import ViewChecklistPage from "./pages/ViewChecklistPage";

// Form Components
import CbamForm from "./components/FormA";
import CbamFormResume from "./components/CbamFormResume";
import AuditList from "./components/AuditList";
import VerifierLanding from "./components/VerifierLanding";
import FormB from "./components/FormB";
import FormC from "./components/FormC";
import FormD from "./components/FormD";
import FormE from "./components/FormE";
import FormF from "./components/FormF";
import SummaryTabs from "./components/SummaryTabs";
import ReportPreview from "./components/ReportPreview";
import CurrencySettings from "./components/CurrencySettings";
import FloatingChatbot from "./components/chatbot/FloatingChatbot";
import { ChatbotAdmin } from "./components/chatbot/ChatbotAdmin";
import TenantLandingManager from "./components/TenantLandingManager";
import TenantHome from "./components/TenantHome";

const AppLayout = ({ children }) => {
  const location = useLocation();

  const isTenantLoginRoute = /^\/t\/[^/]+$/.test(location.pathname);

  return (
    <>
      {!isTenantLoginRoute && <Navbar />}
      {!isTenantLoginRoute && <Toolbar />}

      {children}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AppShell: lives INSIDE <AuthProvider> so it can call useAuth()
// ─────────────────────────────────────────────────────────────────────────────
const AppShell = () => {
  const {
    currentUser,
    isLoggedIn,
    isSuperAdmin,
    isAppAdmin,
    isAppUser,
    tenantId,
    tenantName,
    loading,
    authError,
  } = useAuth();

  // Show spinner while AuthContext is checking session.
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <p>Restoring session...</p>
      </Box>
    );
  }

  // Show auth errors (unauthorized email, inactive account, etc.)
  if (authError) {
    // Use a setTimeout to defer the alert so React doesn't render-loop.
    setTimeout(() => alert(authError), 0);
  }

  return (
    <Router>
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPg />} />
              <Route path="/home" element={<LandingPg />} />
              <Route
                path="/login"
                element={
                  isLoggedIn ? (
                    <Navigate
                      to={isSuperAdmin ? "/tenant-management" : "/audits"}
                      replace
                    />
                  ) : (
                    <Home />
                  )
                }
              />
              <Route path="/tnc" element={<TnC />} />
              <Route path="/t/:tenantId" element={<TenantHome />} />
              {/* PROTECTED ROUTES — strictly role-gated so URL bypass is blocked */}
              {isLoggedIn && (
                <>
                  {/* AppAdmin + AppUser — CBAM + User Checklist */}
                  {(isAppAdmin || isAppUser) && (
                    <>

                      <Route
                        path="/user"
                        element={
                          <UserChecklistWrapper
                            email={currentUser?.email || ""}
                          />
                        }
                      />

                      <Route path="/audits" element={<AuditList />} />

                      <Route
                        path="/audit-dashboard/:auditId"
                        element={<AuditDashboard />}
                      />

                      <Route
                        path="/progress-overview/:auditId"
                        element={<ProgressOverview />}
                      />

                      <Route path="/verifier" element={<VerifierLanding />} />

                      <Route path="/cbam-form" element={<CbamFormResume />} />

                      <Route path="/form-a/:auditId" element={<CbamForm />} />

                      <Route path="/form-b/:auditId" element={<FormB />} />

                      <Route path="/form-c/:auditId" element={<FormC />} />

                      <Route path="/form-d/:auditId" element={<FormD />} />

                      <Route path="/form-e/:auditId" element={<FormE />} />

                      <Route path="/form-f/:auditId" element={<FormF />} />

                      <Route
                        path="/dashboard/:auditId"
                        element={<CbamDashboard />}
                      />

                      <Route
                        path="/summary/:auditId"
                        element={<SummaryTabs />}
                      />

                      <Route
                        path="/report-preview/:auditId"
                        element={<ReportPreview />}
                      />

                      <Route
                        path="/currency-settings/:auditId"
                        element={<CurrencySettings />}
                      />

                      <Route
                        path="/audit-status/:auditId"
                        element={<AuditList />}
                      />
                    </>
                  )}

                  {/* AppAdmin only — User Management + Admin Checklist */}
                  {isAppAdmin && (
                    <>
                      <Route
                        path="/user-management"
                        element={<UserManagement />}
                      />
                      <Route
                        path="/admin"
                        element={
                          <AdminChecklist email={currentUser?.email || ""} />
                        }
                      />
                      <Route
                        path="/admin/checklist/:id"
                        element={<ViewChecklistPage />}
                      />
                    </>
                  )}

                  {/* SuperAdmin only — Tenant Management */}
                  {isSuperAdmin && (
                    <>
                      <Route
                        path="/tenant-management"
                        element={<TenantManagement />}
                      />

                      <Route
                        path="/tenant-landing-pages"
                        element={<TenantLandingManager />}
                      />

                      <Route path="/chatbot-admin" element={<ChatbotAdmin />} />
                    </>
                  )}
                </>
              )}
              {/* Catch-All */}
              <Route
                path="*"
                element={
                  <Navigate
                    to={
                      isLoggedIn
                        ? isSuperAdmin
                          ? "/tenant-management"
                          : "/audits"
                        : "/login"
                    }
                    replace
                  />
                }
              />
            </Routes>
          </Box>

          <Footer />
          <FloatingChatbot />
          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnHover
            draggable
            theme="light"
          />
        </Box>
      </AppLayout>
    </Router>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// App: outer wrapper for providers
// ─────────────────────────────────────────────────────────────────────────────
const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <FormProvider>
          <ProductManualProvider>
            <AppShell />
          </ProductManualProvider>
        </FormProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};
export default App;
